
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CreateWorkspaceDialog } from "./CreateWorkspaceDialog"
import { RenameWorkspaceDialog } from "./RenameWorkspaceDialog"
import { useToast } from "@/hooks/use-toast"
import { Loader2 } from "lucide-react"
import { useWorkspaces } from "@/hooks/use-workspaces"

interface WorkspaceSelectorProps {
  onWorkspaceSelect?: (workspaceId: string) => void;
}

export function WorkspaceSelector({ onWorkspaceSelect }: WorkspaceSelectorProps) {
  const { t } = useTranslation()
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>("")
  const { data: workspaces = [], isLoading } = useWorkspaces()

  useEffect(() => {
    // Select first workspace if none selected
    if (!selectedWorkspace && workspaces[0]?.id) {
      const firstWorkspace = workspaces[0].id
      setSelectedWorkspace(firstWorkspace)
      onWorkspaceSelect?.(firstWorkspace)
    }
  }, [workspaces, selectedWorkspace, onWorkspaceSelect])

  const handleWorkspaceChange = (workspaceId: string) => {
    setSelectedWorkspace(workspaceId)
    onWorkspaceSelect?.(workspaceId)
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{t("common.loading")}</span>
      </div>
    )
  }

  const selectedWorkspaceName = workspaces
    .find(w => w.id === selectedWorkspace)?.name || ""

  const workspacesByOrg = workspaces.reduce((acc: Record<string, typeof workspaces>, workspace) => {
    const orgName = workspace.organizations?.name || 'No Organization'
    if (!acc[orgName]) {
      acc[orgName] = []
    }
    acc[orgName].push(workspace)
    return acc
  }, {})

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center">
        <Select value={selectedWorkspace} onValueChange={handleWorkspaceChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t("common.workspaces.selectWorkspace")} />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(workspacesByOrg).map(([orgName, orgWorkspaces]) => (
              <div key={orgName}>
                <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                  {orgName}
                </div>
                {orgWorkspaces.map((workspace) => (
                  <SelectItem 
                    key={workspace.id} 
                    value={workspace.id}
                    className="pl-4"
                  >
                    {workspace.name}
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        {selectedWorkspace && (
          <RenameWorkspaceDialog
            workspaceId={selectedWorkspace}
            currentName={selectedWorkspaceName}
            onRename={() => {}}
          />
        )}
      </div>
      <CreateWorkspaceDialog onWorkspaceCreated={() => {}} />
    </div>
  )
}
